/**
 * Bidirectional mapping for segment names across different languages.
 * This maps all possible translations of a segment name to a canonical identifier.
 *
 * Usage: Use normalizeSegmentName() to convert any translated segment name to its canonical form
 * before comparing it with configuration values.
 */

export enum CanonicalSegmentName {
    SESTAVLJENA_OMARICA = 'Sestavljena omarica',
    OMARICA = 'Omarica',
    LAMELE = 'Lamele',
    VODILA = 'Vodila',
}

/**
 * Maps all known translations of segment names to their canonical form.
 * Add new translations here as needed.
 */
const segmentTranslationMap: Record<string, CanonicalSegmentName> = {
    // Sestavljena omarica
    'Sestavljena omarica': CanonicalSegmentName.SESTAVLJENA_OMARICA,
    Zusammengesetzkaste: CanonicalSegmentName.SESTAVLJENA_OMARICA,
    'Sestavljeni ormarić': CanonicalSegmentName.SESTAVLJENA_OMARICA,

    // Omarica
    Omarica: CanonicalSegmentName.OMARICA,
    Kasten: CanonicalSegmentName.OMARICA,
    Ormarić: CanonicalSegmentName.OMARICA,

    // Lamele
    Lamele: CanonicalSegmentName.LAMELE,
    Lamellen: CanonicalSegmentName.LAMELE,
    Lamelice: CanonicalSegmentName.LAMELE,

    // Vodila
    Vodila: CanonicalSegmentName.VODILA,
    Führungsschienen: CanonicalSegmentName.VODILA,
    Vodilice: CanonicalSegmentName.VODILA,
};

/**
 * Normalizes a segment name to its canonical form.
 * This allows comparing segment names regardless of the current language.
 *
 * @param segmentName - The translated segment name from the backend
 * @returns The canonical segment name, or the original name if no mapping exists
 */
export function normalizeSegmentName(segmentName: string): string {
    return segmentTranslationMap[segmentName] || segmentName;
}

/**
 * Checks if two segment names are equivalent, regardless of language.
 *
 * @param name1 - First segment name (can be in any language)
 * @param name2 - Second segment name (can be in any language)
 * @returns true if both names refer to the same canonical segment
 */
export function areSegmentNamesEqual(name1: string, name2: string): boolean {
    return normalizeSegmentName(name1) === normalizeSegmentName(name2);
}
